import type { ServedAction } from "@patent-studio/api-types";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Panel } from "@/components/ui/Panel";
import {
  type ChangeDecisions,
  changeAnchor,
  useStepChanges,
} from "../../notifications/StepChanges";
import { SuggestionPanel } from "../../notifications/SuggestionPanel";
import { ElementChip } from "../positions/ElementPicker";

/**
 * One novelty Praxis suggests, drawn as v0.28 draws it below the analysis:
 * outlined, "Suggested" where a position's number would be, its name, the
 * elements it rests on and the change's panel. It carries no verdict: a
 * position added this way arrives confirmed, unassessed until a run rules
 * (lane M decision 22).
 */
function SuggestedNovelty({
  change,
  decide,
}: {
  change: ServedAction;
  decide: ChangeDecisions;
}) {
  return (
    <Panel
      as="li"
      shape="row"
      id={changeAnchor(change.action_id)}
      tabIndex={-1}
      data-testid={`suggested-${change.action_id}`}
      className="mb-2.5 ring-2 ring-primary/60"
    >
      <div className="flex items-center gap-2.5">
        <span className="flex-none text-[11px] font-semibold text-muted-foreground">
          Suggested
        </span>
        <span className="min-w-0 flex-1 text-[13.5px] font-bold">
          {change.after}
        </span>
      </div>
      <div className="mt-3">
        <FieldLabel>Rests on invention elements</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {change.rests_on.map((element) => (
            <ElementChip key={element.text} element={element} />
          ))}
        </div>
      </div>
      <SuggestionPanel change={change} {...decide} />
    </Panel>
  );
}

/** The novelties Praxis suggests adding, below the analysis's rows. */
export function SuggestedNovelties({ taskId }: { taskId: string }) {
  const { changes, decide } = useStepChanges(taskId, "novelty_analysis");
  const added = changes.filter(
    (c) => c.change === "add" && c.target_kind === "position",
  );
  if (added.length === 0) return null;
  return (
    <ul aria-label="Suggested novelties">
      {added.map((change) => (
        <SuggestedNovelty
          key={change.action_id}
          change={change}
          decide={decide(change)}
        />
      ))}
    </ul>
  );
}
